import { useState } from "react";
import SelfServiceButtons from "../SelfServiceButtons";
import { sortByLanguageList } from "../../../../utils/utils";
import { AGREEMENT_POSITIONS, CRS_SURVEY_TEMPLATES, DEFAULT_LANGUAGE_ENGLISH, DISCLAIMER_MSG_CODE, SAVING_DATA } from "../../../../utils/surveyLiterals";
import RichTextEditor from "../../../../components/rich-text-editor/RichTextEditor";
import { Card, CardContent, Grid, Typography, RadioGroup, FormControl, FormLabel, FormControlLabel, SelectField, Option, Radio } from "@deere/fuel-react";
import { validateAgreementStep } from "../../../../utils/validation";
import SelfServiceLoader from "../SelfServiceLoader";

const AgreementStep = ({ error, setError, currentStep, activeStep, setActiveStep, setPayloadData, payloadData, languageList }) => {
  const [selectedLanguage, setSelectedLanguage] = useState(DEFAULT_LANGUAGE_ENGLISH);
  const [loader, setLoader] = useState({isLoading: false, message: ""});

  const fnValidate = (publishHandler) => {
    const error = validateAgreementStep(payloadData);
    error.show
      ? setError(error)
      : (setLoader({ isLoading: true, message: SAVING_DATA }), publishHandler(payloadData));
  };

  const changeHandler = (value) => {
    setError({ show: false, message: "" });
    //quill editor is adding <p> tags for every line added, which is creating unnecessary margins
    //Hence, this logic will remove the unnecessary tag before storing the value in the payload
    let formattedValue = value.replace(/<p><br><\/p>/g, "<br>").replace(/<p([^>]*)>(.*?)<\/p>/g, (match, attributes, content) => {
      if (attributes.trim()) {
        return `<span${attributes}>${content}</span>`;
      }
      return `${content}`;
    });
    formattedValue = formattedValue === "<br>" ? "" : formattedValue;
    setPayloadData((prevState) => {
      const newAgreementData = prevState.agreementData;
      const lngIndex = languageList.findIndex((lng) => lng.language === selectedLanguage);
      if (lngIndex > -1) {
        const agrIndex = newAgreementData.findIndex((agr) => agr.languageCd.split("_")[0] === languageList[lngIndex].languageCd.split("_")[0]);
        if (formattedValue !== "") {
          agrIndex > -1 ? (newAgreementData[agrIndex].text = formattedValue) : newAgreementData.push({ languageCd: languageList[lngIndex].languageCd, code: DISCLAIMER_MSG_CODE, text: formattedValue });
        } else if (agrIndex > -1) {
          newAgreementData.splice(agrIndex, 1);
        }
      }
      return { ...prevState, agreementData: newAgreementData };
    });
  };

  const radioChangeHandler = (e) => {
    setError({ show: false, message: "" });
    setPayloadData((prevState) => {
      return { ...prevState, agreementPosition: e.target.value };
    });
  };

  const getAgreementValue = () => {
    const lngIndex = languageList.findIndex((lng) => lng.language === selectedLanguage);
    if (payloadData && payloadData.agreementData && lngIndex > -1) {
      const agrIndex = payloadData.agreementData.findIndex((agr) => agr.languageCd.split("_")[0] === languageList[lngIndex].languageCd.split("_")[0]);
      return agrIndex > -1 ? payloadData.agreementData[agrIndex].text : "";
    }
    return "";
  };

  return (
    <>
      <Card>
        <CardContent>
          {loader.message !== "" && <SelfServiceLoader loader={loader} />}
          {loader.message === "" && <Grid container direction="column" justifyContent="space-between" minHeight={"60vh"} rowSpacing={4}>
            <Grid item>
              <Typography variant="h1">
                {currentStep.step} - {currentStep.title}
              </Typography>
              <Typography variant="subtitle">
                This is an optional step. Here you can add a Disclaimer/Agreement message which will be displayed on the top of your survey screen or on the survey-submission popup, based on your
                selection.
              </Typography>
            </Grid>
            {payloadData.templateType === CRS_SURVEY_TEMPLATES.PART ? (
              <>
                <Grid item mt={4}>
                  <FormControl sx={{ "& .MuiFormLabel-root": { fontSize: "12px" } }}>
                    <FormLabel>Where do you want the message to get displayed ?</FormLabel>
                    <RadioGroup required>
                      <FormControlLabel
                        control={<Radio />}
                        label="Survey Screen"
                        name="agreementPosition"
                        sx={{ "& .MuiFormControlLabel-label": { fontSize: "12px!important" } }}
                        onChange={radioChangeHandler}
                        value={AGREEMENT_POSITIONS.SURVEY_SCREEN}
                        checked={payloadData.agreementPosition === AGREEMENT_POSITIONS.SURVEY_SCREEN}
                      />
                      <FormControlLabel
                        control={<Radio />}
                        label="Submission Popup"
                        name="agreementPosition"
                        sx={{ "& .MuiFormControlLabel-label": { fontSize: "12px!important" } }}
                        onChange={radioChangeHandler}
                        value={AGREEMENT_POSITIONS.SUBMISSION_POPUP}
                        checked={payloadData.agreementPosition === AGREEMENT_POSITIONS.SUBMISSION_POPUP}
                      />
                      <FormControlLabel
                        control={<Radio />}
                        label="Both"
                        name="agreementPosition"
                        sx={{ "& .MuiFormControlLabel-label": { fontSize: "12px!important" } }}
                        onChange={radioChangeHandler}
                        value={AGREEMENT_POSITIONS.BOTH}
                        checked={payloadData.agreementPosition === AGREEMENT_POSITIONS.BOTH}
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>
                <Grid item>
                  <SelectField
                    label="Language"
                    sx={{ "& .MuiFormLabel-root": { fontSize: "12px" }, marginBottom: "10px" }}
                    value={selectedLanguage}
                    onChange={(e) => setSelectedLanguage(e.target.value)}
                    disabled={payloadData.agreementPosition === ""}
                  >
                    {sortByLanguageList(payloadData.language, languageList).map((lang) => (
                      <Option key={lang.text} value={lang.text}>
                        {lang.text}
                      </Option>
                    ))}
                  </SelectField>
                  <Typography variant="label" sx={{ fontSize: "12px!important" }} fontWeight="regular">
                    Agreement Message
                  </Typography>
                  <RichTextEditor
                    defaultValue={getAgreementValue()}
                    changeHandler={changeHandler}
                    valueResetDependency={selectedLanguage}
                    disabled={payloadData.agreementPosition === ""}
                    link={true}
                    reset={true}
                  />
                </Grid>
              </>
            ) : (
              <Grid item justifyItems="center">
                <Typography>This step is only required for Part-Related Templates.</Typography>
              </Grid>
            )}
            {error.show && (
              <Typography variant="subtitle1" color="#C21020" textAlign="right" marginTop={2}>
                {error.message}
              </Typography>
            )}
            <Grid item pr={5}>
              <Grid container justifyContent="flex-end" mt={3}>
                <SelfServiceButtons
                  activeStep={activeStep}
                  setActiveStep={setActiveStep}
                  fnValidate={fnValidate}
                  payloadData={payloadData}
                  setPayloadData={setPayloadData}
                  setLoader={setLoader}
                />
              </Grid>
            </Grid>
          </Grid>}
        </CardContent>
      </Card>
    </>
  );
};

export default AgreementStep;
